import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    title: 'Someone cared enough to say this to you.',
    prompt: 'Who in your life deserves to hear something from you today?',
    placeholder: 'Type their name...',
    cta: 'Start Your Message',
    suggestions: ['Mom', 'Dad', 'Best Friend', 'Grandmother'],
  },
  es: {
    title: 'Alguien se preocupó lo suficiente como para decirte esto.',
    prompt: '¿Quién en tu vida merece escuchar algo de ti hoy?',
    placeholder: 'Escribe su nombre...',
    cta: 'Comienza Tu Mensaje',
    suggestions: ['Mamá', 'Papá', 'Mejor Amigo/a', 'Abuela'],
  },
  fr: {
    title: 'Quelqu\'un a pris le temps de vous dire cela.',
    prompt: 'Qui dans votre vie mérite d\'entendre quelque chose de vous aujourd\'hui ?',
    placeholder: 'Tapez leur nom...',
    cta: 'Commencez Votre Message',
    suggestions: ['Maman', 'Papa', 'Meilleur(e) ami(e)', 'Grand-mère'],
  },
  pt: {
    title: 'Alguém se importou o suficiente para dizer isso a você.',
    prompt: 'Quem na sua vida merece ouvir algo de você hoje?',
    placeholder: 'Digite o nome...',
    cta: 'Comece Sua Mensagem',
    suggestions: ['Mãe', 'Pai', 'Melhor Amigo/a', 'Avó'],
  },
  ar: {
    title: 'شخص ما اهتم بما يكفي ليقول لك هذا.',
    prompt: 'من في حياتك يستحق أن يسمع شيئًا منك اليوم؟',
    placeholder: 'اكتب اسمه...',
    cta: 'ابدأ رسالتك',
    suggestions: ['أمي', 'أبي', 'أفضل صديق', 'جدتي'],
  },
  ja: {
    title: '誰かがあなたにこれを伝えたいと思いました。',
    prompt: 'あなたの人生で、今日何かを聞く価値のある人は誰ですか？',
    placeholder: '名前を入力...',
    cta: 'メッセージを始める',
    suggestions: ['お母さん', 'お父さん', '親友', 'おばあちゃん'],
  },
  ko: {
    title: '누군가 당신에게 이것을 전하고 싶었습니다.',
    prompt: '당신의 삶에서 오늘 무언가를 들을 자격이 있는 사람은 누구인가요?',
    placeholder: '이름을 입력하세요...',
    cta: '메시지 시작하기',
    suggestions: ['엄마', '아빠', '친한 친구', '할머니'],
  },
  id: {
    title: 'Seseorang cukup peduli untuk mengatakan ini kepadamu.',
    prompt: 'Siapa dalam hidupmu yang layak mendengar sesuatu darimu hari ini?',
    placeholder: 'Ketik nama mereka...',
    cta: 'Mulai Pesanmu',
    suggestions: ['Ibu', 'Ayah', 'Sahabat', 'Nenek'],
  },

  hi: {
    title: 'किसी ने आपकी परवाह की और यह आपसे कहा।',
    prompt: 'आपके जीवन में कौन आज आपसे कुछ सुनने का हकदार है?',
    placeholder: 'उनका नाम टाइप करें...',
    cta: 'अपना संदेश शुरू करें',
    suggestions: ['Mom', 'Dad', 'Best Friend', 'Grandmother'],
  },
};

interface ReflectionNamePromptProps {
  source?: string;
}

export function ReflectionNamePrompt({ source = 'announcement_view' }: ReflectionNamePromptProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const tracked = useRef(false);

  // Rotate placeholder suggestions
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIdx((prev) => (prev + 1) % t.suggestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [t.suggestions.length]);

  useEffect(() => {
    if (!tracked.current) {
      tracked.current = true;
      supabase.from('events' as any).insert({
        event_name: 'reflection_prompt_viewed',
        properties: { source },
      }).then(() => {});
    }
  }, [source]);

  const handleStart = () => {
    supabase.from('events' as any).insert({
      event_name: 'reflection_prompt_started',
      properties: { source, has_name: !!name.trim() },
    }).then(() => {});

    // Navigate to create flow with pre-filled recipient name
    const params = new URLSearchParams();
    if (name.trim()) {
      params.set('recipient', name.trim());
    }
    params.set('ref', 'recipient');
    navigate(`/signup?${params.toString()}`);
  };

  const dynamicPlaceholder = `${t.placeholder.replace('...', '')} ${t.suggestions[placeholderIdx]}...`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="py-10 px-4"
    >
      <div className="max-w-md mx-auto text-center space-y-6">
        {/* Emotional icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 mx-auto"
        >
          <Heart className="h-7 w-7 text-primary" fill="currentColor" />
        </motion.div>

        {/* Title */}
        <h3 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
          {t.title}
        </h3>

        {/* Reflective question */}
        <p className="text-base text-muted-foreground/80 italic leading-relaxed">
          {t.prompt}
        </p>

        {/* Name input */}
        <div className="space-y-3 pt-2">
          <Input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={dynamicPlaceholder}
            className="text-center text-lg h-12 rounded-2xl bg-background/60 border-border/50 focus:border-primary/50 transition-all"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) handleStart();
            }}
          />

          {/* Suggestion chips */}
          <div className="flex flex-wrap justify-center gap-2">
            {t.suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setName(suggestion);
                  inputRef.current?.focus();
                }}
                className="px-3 py-1 text-xs rounded-full border border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-primary/5 transition-all duration-200"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>

        {/* CTA button */}
        <Button
          size="lg"
          onClick={handleStart}
          className="gap-2 rounded-2xl shadow-lg shadow-primary/20 group w-full sm:w-auto px-8"
        >
          {t.cta}
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </motion.div>
  );
}
